import classNames from "classnames"
import React, { useRef, useState } from "react"
import { EffectFade } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

import AdaptiveWrapText from "../AdaptiveWrapText";

import { showElemFullyInScrollContainer } from "../../../utils/showElemFullyInScrollContainer"

import styles from "./styles.module.scss"
import { useHorizontalScrollOnWheel } from "../../../hooks/useHorizontalScrollOnWheel"

import "swiper/css"
import "swiper/scss/effect-fade"

export enum HIDE_BADGES {
	VERTICAL = "vertical",
	MOBILE = "mobile",
}

interface Feature {
	icon: JSX.Element
	name: string
	label?: string
	description?: string | JSX.Element
	img?: JSX.Element
	badge?: JSX.Element
	mediaContent?: JSX.Element
}

export type ColorTheme =
	| "fuchsia"
	| "fuchsia-invert"
	| "blue-sky"
	| "cyan"
	| "purple-light"
export type HeaderPlacement = "left" | "top"

interface Props {
	className?: string
	rootClass?: string
	containerClass?: string
	wrapperClass?: string
	additionalFeaturesListClass?: string
	leftColumnClass?: string
	sliderClass?: string
	data: {
		title: string | JSX.Element
		titleTablet?: string | JSX.Element
		titleMobile?: string | JSX.Element
		description?: string
		category?: string
		headerPlacement: HeaderPlacement
		features: Feature[]
	}
	hideBadges?: HIDE_BADGES
	theme: ColorTheme
	isTabsInColumns?: boolean
	isFullWidth?: boolean
	isShowDescription?: boolean
}

export default function TabsFeatures(props: Props) {
	const rootClass = props.rootClass || styles.tabsFeatures
	const containerClass = props.containerClass || styles.tabsFeatures__container
	const wrapperClass = props.wrapperClass || styles.tabsFeatures__wrapper
	const hideBadges =
		props.hideBadges !== undefined ? props.hideBadges : HIDE_BADGES.VERTICAL

	const swiperRef = useRef()
	const containerRef = useRef<HTMLDivElement>(null)

	useHorizontalScrollOnWheel(containerRef, "auto")

	const [selectedFeature, setSelectedFeature] = useState(props.data.features[0])

	const onClickFeature = (feature, evt) => {
		swiperRef.current.slideTo(props.data.features.indexOf(feature))

		showElemFullyInScrollContainer(
			evt.target.closest("li"),
			evt.target.closest("li").parentNode
		)

		setSelectedFeature(feature)
	}

	const renderFeature = feature => {
		return (
			<>
				{feature.mediaContent ? (
					feature.mediaContent
				) : (
					<figure className={styles.tabsFeaturesAbout__img}>
						{feature.img}
					</figure>
				)}

				{feature.badge && (
					<div className={styles.tabsFeaturesAbout__badge}>{feature.badge}</div>
				)}

				{feature.label && (
					<noindex>
						<p className={styles.tabsFeaturesAbout__label}>{feature.label}</p>
					</noindex>
				)}

				<div
					className={classNames({
						[styles.tabsFeatures__descriptionContainer_vertical]:
							hideBadges === HIDE_BADGES.VERTICAL,
						[styles.tabsFeatures__descriptionContainer_mobile]:
							hideBadges === HIDE_BADGES.MOBILE,
					})}
				>
					{feature.description ? (
						typeof feature.description === "string" ? (
							<div
								className={styles.tabsFeatures__descriptionItem}
								dangerouslySetInnerHTML={{ __html: feature.description }}
							/>
						) : (
							<div className={styles.tabsFeatures__descriptionItem}>
								{feature.description}
							</div>
						)
					) : (
						""
					)}
				</div>
			</>
		)
	}

	return (
		<section
			className={classNames(rootClass, props.className, {
				[styles.tabsFeaturesBlueSky]: props.theme === "blue-sky",
				[styles.tabsFeaturesFuchsia]: props.theme === "fuchsia",
				[styles.tabsFeaturesFuchsiaInvert]: props.theme === "fuchsia-invert",
				[styles.tabsFeaturesCyan]: props.theme === "cyan",
				[styles.tabsFeaturesPurpleLight]: props.theme === "purple-light",
			})}
		>
			<div className={containerClass}>
				{props.data.headerPlacement === "top" && (
					<>
						<span className={styles.tabsFeatures__category}>
							{props.data.category}
						</span>
						<AdaptiveWrapText
							desktop={props.data.title}
							tablet={props.data.titleTablet}
							mobile={props.data.titleMobile}
							titleClasses={styles.tabsFeatures__title}
							noBreakWordsMobile
						>
							<h2 className={styles.tabsFeatures__title}>{props.data.title}</h2>
						</AdaptiveWrapText>
					</>
				)}

				<div className={wrapperClass}>
					<div
						className={classNames(
							styles.tabsFeatures__left,
							{ [styles.tabsFeatures__leftFullWidth]: props.isFullWidth },
							props.leftColumnClass
						)}
					>
						{props.data.headerPlacement === "left" && (
							<div className={styles.tabsFeatures__header}>
								<span className={styles.tabsFeatures__category}>
									{props.data.category}
								</span>
								<AdaptiveWrapText
									desktop={props.data.title}
									tablet={props.data.titleTablet}
									mobile={props.data.titleMobile}
									titleClasses={styles.tabsFeatures__title}
									noBreakWordsMobile
								>
									<h2 className={styles.tabsFeatures__title}>{props.data.title}</h2>
								</AdaptiveWrapText>
								{props.data.description && <p className={styles.tabsFeatures__desc}>{props.data.description}</p>}
							</div>
						)}

						<ul
							className={classNames(
								styles.tabsFeaturesList,
								props.additionalFeaturesListClass,
								{
									[styles.tabsFeaturesListColumns]: props.isTabsInColumns,
									[styles.tabsFeaturesListFullWidth]: props.isFullWidth,
								}
							)}
							ref={containerRef}
						>
							{props.data.features.map(feature => (
								<li
									key={feature.name}
									className={classNames(styles.tabsFeaturesList__item, {
										[styles.tabsFeaturesList__itemSelected]:
											feature?.name === selectedFeature?.name,
									})}
									onClick={e => onClickFeature(feature, e)}
								>
									{feature.icon}

									<span>{feature.name}</span>
								</li>
							))}
						</ul>

						{props.isShowDescription && (
							<div
								className={styles.tabsFeatures__descriptionContainer_desktop}
							>
								{props.data.features.map(feature => (
									<div
										key={feature.name}
										className={classNames(
											styles.tabsFeatures__description,
											styles.tabsFeatures__descriptionDesktop,
											{
												[styles.tabsFeatures__description_selected]:
													feature?.name === selectedFeature?.name,
											}
										)}
									>
										{feature.description ? (
											typeof feature.description === "string" ? (
												<div
													className={styles.tabsFeatures__descriptionItem}
													dangerouslySetInnerHTML={{
														__html: feature.description,
													}}
												/>
											) : (
												<div className={styles.tabsFeatures__descriptionItem}>
													{feature.description}
												</div>
											)
										) : (
											""
										)}
									</div>
								))}
							</div>
						)}
					</div>

					<Swiper
						className={classNames(
							styles.tabsFeaturesAbout__swiper,
							props.sliderClass
						)}
						onSwiper={swiper => {
							swiperRef.current = swiper
						}}
						onSlideChange={swiper => {
							setSelectedFeature(props.data.features[swiper.activeIndex])
						}}
						style={{
							width: "100%",
						}}
						init={false}
						modules={[EffectFade]}
						autoHeight={true}
						effect={"fade"}
						fadeEffect={{
							crossFade: true,
						}}
					>
						{props.data.features.map(feature => (
							<SwiperSlide key={feature.name}>
								<div
									className={classNames(
										styles.tabsFeaturesAbout,
										styles.tabsFeaturesAboutSelected,
										{
											[styles.tabsFeaturesAbout_badges_hideOnVertical]:
												hideBadges === HIDE_BADGES.VERTICAL,
											[styles.tabsFeaturesAbout_badges_hideOnMobile]:
												hideBadges === HIDE_BADGES.MOBILE,
										}
									)}
								>
									{renderFeature(feature)}
								</div>
							</SwiperSlide>
						))}
					</Swiper>

					<noindex className={styles.tabsFeaturesAbout__noSwiper}>
						<div>
							{props.data.features.map(feature => (
								<div
									key={feature.name}
									className={classNames(styles.tabsFeaturesAbout, {
										[styles.tabsFeaturesAboutSelected]:
											feature?.name === selectedFeature?.name,
										[styles.tabsFeaturesAbout_badges_hideOnVertical]:
											hideBadges === HIDE_BADGES.VERTICAL,
										[styles.tabsFeaturesAbout_badges_hideOnMobile]:
											hideBadges === HIDE_BADGES.MOBILE,
									})}
								>
									{renderFeature(feature)}
								</div>
							))}
						</div>
					</noindex>
				</div>
			</div>
		</section>
	)
}
